import { useEffect, useState } from "react";
import { CatalogPicker } from "@core/components/CatalogPicker";
import { List, ListRow } from "@core/components/List";
import { LongText } from "@core/components/LongText";
import { ElaboratedRow } from "./elaboration";
import { ChosenRow } from "./parts";
import { page, useSm } from "./view";

/** Step 8: the moon sign first, then voluntary weaknesses (GRW p. 24, 81–84). */
export function MoonSignStep() {
  const { view, confirm, apply, language } = useSm();
  const [picking, setPicking] = useState(false);
  const [name, setName] = useState("");
  const signs = view.catalog?.moonSigns ?? [];
  const chosen = view.moonSign ? signs.find((s) => s.id === view.moonSign?.id) : undefined;
  const [choosing, setChoosing] = useState(!view.moonSign);
  useEffect(() => setChoosing(!view.moonSign), [view.moonSign?.id]);

  return (
    <div className="stack">
      <section className="stack">
        <h3 className="section-title">1. Mondzeichen</h3>
        {chosen && !choosing ? (
          <ChosenRow
            kicker="Mondzeichen"
            source={page(chosen.source)}
            title={chosen.name}
            description={chosen.description}
            facts={chosen.gifts.find((g) => g.heroLevel === 1)?.summary ?? chosen.summary ?? undefined}
            onChange={() => setChoosing(true)}
          />
        ) : (
          <>
        <div className="row spread">
          <p className="muted small">Der Mondsplitter verbindet den Abenteurer mit einem Schicksal. Jedes Mondzeichen hat eine Gabe, die Splitterpunkte kostet und mit dem Heldengrad wächst.</p>
          {chosen && (
            <button type="button" className="link" onClick={() => setChoosing(false)}>
              Abbrechen
            </button>
          )}
        </div>
        <div className="module-grid">
          {signs
            .map((sign) => {
              const selected = sign.id === chosen?.id;
              return (
                <article key={sign.id} className={`card module-card${selected ? " selected" : ""}`}>
                  <header>
                    <h3>{sign.name}</h3>
                    <span className="source">{page(sign.source)}</span>
                  </header>
                  {sign.summary && <p className="muted small">{sign.summary}</p>}
                  <LongText value={sign.description} language={language} />
                  <dl className="facts">
                    {sign.gifts.map((gift, i) => (
                      <GiftRow key={i} heroLevel={gift.heroLevel} summary={gift.summary} />
                    ))}
                  </dl>
                  <button
                    type="button"
                    className={`button ${selected ? "secondary" : "primary"}`}
                    disabled={selected}
                    aria-label={`${sign.name} wählen`}
                    onClick={() => {
                      window.scrollTo({ top: 0 });
                      confirm({ type: "sm.setMoonSign", payload: { id: sign.id } }, `Mondzeichen ${sign.name}`);
                    }}
                  >
                    {selected ? "Gewählt" : "Wählen"}
                  </button>
                </article>
              );
            })}
        </div>
          </>
        )}
      </section>

      <section className="card stack">
        <h3>2. Schwächen (freiwillig)</h3>
        <p className="muted small">Schwächen bringen keine Punkte, aber im Spiel Splitterpunkte zurück, wenn sie den Abenteurer in Schwierigkeiten bringen (GRW S. 83).</p>
        <List aria-label="Schwächen">
          {view.weaknesses.length === 0 && <li className="list-empty">Noch keine Schwäche gewählt.</li>}
          {view.weaknesses.map((w, index) => (
            <ElaboratedRow
              key={index}
              title={w.name}
              subject={w.name}
              elaboration={w}
              onSave={({ note, details }) => apply({ type: "sm.updateWeakness", payload: { index, note, details } })}
              actions={
                <button type="button" className="link danger" onClick={() => void apply({ type: "sm.removeWeakness", payload: { index } })}>
                  Entfernen
                </button>
              }
            />
          ))}
        </List>
        <div className="row">
          <button type="button" className="button secondary" onClick={() => setPicking(true)}>
            + Aus den Beispielen wählen
          </button>
        </div>
        <div className="row">
          <input className="grow" placeholder="Eigene Schwäche benennen" value={name} onChange={(e) => setName(e.target.value)} aria-label="Eigene Schwäche" />
          <button
            type="button"
            className="button secondary"
            disabled={!name.trim()}
            onClick={() => {
              void apply({ type: "sm.addWeakness", payload: { name: name.trim() } });
              setName("");
            }}
          >
            Hinzufügen
          </button>
        </div>
      </section>
      {picking && (
        <CatalogPicker
          type="weaknesses"
          title="Schwäche wählen"
          hint="Beispiele aus dem GRW (S. 84). Eine eigene Schwäche kannst du auch frei benennen."
          onClose={() => setPicking(false)}
          onPick={(item) => {
            setPicking(false);
            void apply({ type: "sm.addWeakness", payload: { id: item.id } });
          }}
        />
      )}
    </div>
  );
}

function GiftRow({ heroLevel, summary }: { heroLevel: number; summary: string }) {
  return (
    <>
      <dt>HG {heroLevel}</dt>
      <dd className="small">{summary}</dd>
    </>
  );
}

export function WeaknessList() {
  const { view } = useSm();
  return (
    <List>
      {view.weaknesses.map((w, i) => (
        <ListRow key={i} title={w.name} subtitle={w.note ?? undefined} />
      ))}
    </List>
  );
}
